import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'

import { motion } from 'motion/react'

/** Concurrent waves kept alive; a rapid clicker drops the oldest past this. */
export const MAX_RIPPLES = 4

const RIPPLE_TRANSITION = { duration: 0.6, ease: 'easeOut' } as const
const RIPPLE_INITIAL = { opacity: 0.25, scale: 0 }
const RIPPLE_ANIMATE = { opacity: 0, scale: 1 }

export interface RippleGeometry {
  x: number
  y: number
  size: number
}

interface Ripple extends RippleGeometry {
  id: number
}

export interface ButtonRippleHandle {
  spawn: (geometry: RippleGeometry) => void
}

interface ButtonRippleProps {
  initialRipples: RippleGeometry[]
  enabled: boolean
}

export function measureRipple(
  event: React.PointerEvent<HTMLElement>
): RippleGeometry {
  const button = event.currentTarget
  const rect = button.getBoundingClientRect()
  /* The wave is positioned inside this layer, whose containing block is the
     button's *padding* box, while getBoundingClientRect reports the *border*
     box. Subtracting the border widths (clientLeft/clientTop) is what keeps
     the wave centred on the pointer instead of 1px off it. */
  const x = event.clientX - rect.left - button.clientLeft
  const y = event.clientY - rect.top - button.clientTop
  const width = button.clientWidth
  const height = button.clientHeight
  /* Diameter spans twice the distance to the farthest corner, so the wave
     still covers the box when the press lands near an edge. */
  const size =
    2 *
    Math.max(
      Math.hypot(x, y),
      Math.hypot(width - x, y),
      Math.hypot(x, height - y),
      Math.hypot(width - x, height - y)
    )
  return { x, y, size }
}

/**
 * Mounted on the first pointer interaction. Subsequent presses update only
 * this subtree through the imperative handle, leaving the control untouched.
 *
 * The wave is `bg-current`, so it inherits each hierarchy's text colour: white
 * over primary's dark fill, ink over secondary/tertiary's light ones.
 */
export const ButtonRipple = forwardRef<ButtonRippleHandle, ButtonRippleProps>(
  ({ initialRipples, enabled }, ref) => {
    const [ripples, setRipples] = useState<Ripple[]>(() =>
      initialRipples.map((geometry, id) => ({ ...geometry, id }))
    )
    const nextId = useRef(initialRipples.length)

    useEffect(() => {
      if (!enabled) setRipples([])
    }, [enabled])

    useImperativeHandle(
      ref,
      () => ({
        spawn: geometry => {
          const wave = { ...geometry, id: nextId.current++ }
          setRipples(prev => [...prev, wave].slice(-MAX_RIPPLES))
        }
      }),
      []
    )

    const remove = (id: number) =>
      setRipples(prev => prev.filter(r => r.id !== id))

    if (!enabled || ripples.length === 0) return null

    return (
      <span
        data-slot='button-ripple'
        aria-hidden='true'
        className='pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]'
      >
        {ripples.map(({ id, x, y, size }) => (
          <motion.span
            key={id}
            className='absolute rounded-full bg-current'
            style={{
              left: x - size / 2,
              top: y - size / 2,
              width: size,
              height: size
            }}
            initial={RIPPLE_INITIAL}
            animate={RIPPLE_ANIMATE}
            transition={RIPPLE_TRANSITION}
            onAnimationComplete={() => remove(id)}
          />
        ))}
      </span>
    )
  }
)
ButtonRipple.displayName = 'ButtonRipple'
